import { capitalCase, formatMoney, uploadsUrl, getGarmentByAbbreviation } from '@trinity/utils'
import { useEffect, useState } from 'react'
import { useFetcher } from 'react-router'
import { useGlobalState, useZoomDialog } from '../../hooks'
import {
  ActionGutter,
  Box,
  Button,
  Close,
  Dialog,
  Divider,
  IconButton,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '../../components'
import { ImageNotFound } from '../../assets'

export type DrawerTab = 'STATUS' | 'OPTIONS' | 'MEASUREMENTS' | 'IMAGES'

interface GarmentDrawerProps {
  garment: TrinityAPI.GarmentExtendedType
  order: TrinityAPI.OrderExtendedType
  open: boolean
  onClose: () => void
}

type GarmentDrawerFetcherData = {
  intent?: string
  options: TrinityAPI.GarmentOptionType[] | null
  measurements: TrinityAPI.GarmentMeasurementType[] | null
  images: TrinityAPI.UploadedImageType[] | null
  customerImages: TrinityAPI.UploadedImageType[] | null
}

export function GarmentDrawer({ garment, order, open, onClose }: GarmentDrawerProps) {
  const fetcher = useFetcher<GarmentDrawerFetcherData>()
  const { onMobile, onSkiffle } = useGlobalState()

  const [activeTab, setActiveTab] = useState<DrawerTab>('STATUS')

  const [activeGarmentId, setActiveGarmentId] = useState<number | null>(null)

  useEffect(() => {
    if (open && activeGarmentId !== garment.id) {
      fetcher.submit(
        { intent: 'getInfo', garmentId: garment.id, customerId: order.customer.id, garmentType: garment.garmentType },
        { method: 'post' },
      )
      // eslint-disable-next-line
      setActiveGarmentId(garment.id)
    }

    // reset state on close so if another garment is opened, it starts on the status tab
    return () => {
      if (activeGarmentId !== garment.id) {
        setActiveTab('STATUS')
      }
    }
  }, [open, garment.id, garment.garmentType, order.customer.id, fetcher, activeGarmentId, setActiveGarmentId])

  const payload = fetcher.data

  const options = payload?.options
  const measurements = payload?.measurements
  const images = payload?.images
  const customerImages = payload?.customerImages
  const garmentType = getGarmentByAbbreviation(garment.garmentType)
  const displayedTab = {
    STATUS: <RenderStatusTab garment={garment} order={order} />,
    OPTIONS: <RenderOptionsTab options={options} />,
    MEASUREMENTS: <RenderMeasurementsTab measurements={measurements} />,
    IMAGES: (
      <RenderImagesTab
        garment={garment}
        customerId={order.customer.id}
        images={images}
        customerImages={customerImages ?? []}
        fetcher={fetcher}
      />
    ),
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      noCloseButton
      fullScreen={onMobile}
      slotProps={{
        paper: {
          sx: {
            height: '100dvh',
            maxHeight: '100dvh',
            minWidth: onMobile ? '100%' : 600,
            width: onMobile ? '100%' : 'clamp(600px, 42vw, 820px)',
            m: 0,
            borderRadius: 0,
          },
        },
      }}
      sx={{
        '& .MuiDialog-container': { justifyContent: 'flex-end', alignItems: 'stretch', height: '100dvh' },
      }}
    >
      <Stack
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'common.white', pt: onSkiffle ? 8 : 1.5 }}
        >
          <Stack spacing={0.25}>
            <Typography color='common.white' variant='h6'>
              {garment.id}
            </Typography>
            <Typography variant='caption' sx={{ opacity: 0.9 }}>
              {garmentType?.alternateName ?? garmentType?.formalName ?? garment.garmentType}
            </Typography>
          </Stack>
          <IconButton aria-label='Close drawer' onClick={onClose} sx={{ color: 'common.white' }} size='small'>
            <Close />
          </IconButton>
        </Stack>
        <GarmentPreviewSection
          imageSrc={garment?.renderedUri?.replace('width=500', 'width=1000') ?? garment?.fabric?.url ?? ImageNotFound}
          label={garment?.fabric?.description ?? ''}
        />
        <Stack direction='row' spacing={1} sx={{ px: 2, py: 1, overflowX: 'auto' }}>
          <DrawerTabPill active={activeTab === 'STATUS'} label='Status' onClick={() => setActiveTab('STATUS')} />
          <DrawerTabPill active={activeTab === 'OPTIONS'} label='Options' onClick={() => setActiveTab('OPTIONS')} />
          <DrawerTabPill
            active={activeTab === 'MEASUREMENTS'}
            label='Measurements'
            onClick={() => setActiveTab('MEASUREMENTS')}
          />
          <DrawerTabPill active={activeTab === 'IMAGES'} label='Images' onClick={() => setActiveTab('IMAGES')} />
        </Stack>
        <Divider />
        <Box sx={{ px: 2, py: 2, mb: { mobile: 4, tablet: 8 }, overflowY: 'auto', flex: 1 }}>
          {displayedTab[activeTab]}
        </Box>
      </Stack>
    </Dialog>
  )
}

function RenderOptionsTab({ options }: { options: TrinityAPI.GarmentOptionType[] | null | undefined }) {
  if (!options || options.length === 0) {
    return (
      <Stack spacing={2} alignItems='center' sx={{ py: 4 }}>
        <Typography variant='body3' color='text.secondary' align='center'>
          No options selected for this garment
        </Typography>
      </Stack>
    )
  }

  return (
    <DetailRowsList
      rows={options.map(o => ({
        label: o.option.description,
        value: capitalCase(o.text ?? o.optionValue?.optionValue ?? '--'),
      }))}
    />
  )
}

function RenderMeasurementsTab({
  measurements,
}: {
  measurements: TrinityAPI.GarmentMeasurementType[] | null | undefined
}) {
  if (!measurements || measurements.length === 0) {
    return (
      <Stack spacing={2} alignItems='center' sx={{ py: 4 }}>
        <Typography variant='body3' color='text.secondary' align='center'>
          No measurements available for this garment
        </Typography>
      </Stack>
    )
  }

  return (
    <DetailRowsList
      rows={measurements.map(m => ({
        label: m.measurement.description,
        value: capitalCase(m.text ?? m.measurementValue?.value ?? '--'),
      }))}
    />
  )
}

function RenderStatusTab({
  garment,
  order,
}: {
  garment: TrinityAPI.GarmentExtendedType
  order: TrinityAPI.OrderExtendedType | null
}) {
  const { user } = useGlobalState()

  const garmentType = getGarmentByAbbreviation(garment.garmentType)
  const canViewPrices = user.permissions.nav.includes('can_view_price_list')

  return (
    <Stack spacing={2}>
      <Stack spacing={1} sx={{ alignSelf: 'flex-start' }}>
        <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
          STATUS
        </Typography>
        <StatusChip status={garment.orderStatus.description} />
      </Stack>
      <Stack spacing={1.5}>
        <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
          DETAILS
        </Typography>
        <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={1.5}>
          <StatusCard
            label='Garment Type'
            value={garmentType?.alternateName ?? garmentType?.formalName ?? garment.garmentType}
          />
        </Stack>
      </Stack>
      {canViewPrices && (
        <Stack spacing={1} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: 'n100' }}>
          <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
            PRICING
          </Typography>
          <KeyValue label='Base' value={formatMoney(order?.currency.name ?? 'USD', garment?.price ?? '0')} />
          <KeyValue label='Options' value={formatMoney(order?.currency.name ?? 'USD', garment?.optionCost ?? '0')} />
          <Divider sx={{ my: 0.75 }} />
          <KeyValue
            label='Total'
            value={formatMoney(
              order?.currency.name ?? 'USD',
              (Number(garment?.price ?? 0) + Number(garment?.optionCost ?? 0)).toString(),
            )}
            bold
          />
        </Stack>
      )}
      <Stack spacing={1} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: 'n100' }}>
        <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
          ISSUES
        </Typography>
        <Typography variant='body3' color='text.secondary'>
          No issues reported
        </Typography>
      </Stack>
    </Stack>
  )
}

function RenderImagesTab({
  garment,
  customerId,
  images,
  customerImages,
  fetcher,
}: {
  garment: TrinityAPI.GarmentExtendedType
  customerId: number
  images: TrinityAPI.UploadedImageType[] | null | undefined
  customerImages: TrinityAPI.UploadedImageType[] | null
  fetcher: ReturnType<typeof useFetcher>
}) {
  const [imageToDelete, setImageToDelete] = useState<number | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const closeUploadDialog = () => {
    setUploadOpen(false)
    setPendingFile(null)
  }

  const handleFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setPendingFile(file ?? null)
  }

  const onAddImage = () => setUploadOpen(true)

  const handleCloseDeleteDialog = () => {
    setImageToDelete(null)
  }

  if (images == null)
    return (
      <Stack spacing={2} alignItems='center' sx={{ py: 4 }}>
        <Typography variant='body3' color='text.secondary' align='center'>
          ...
        </Typography>
      </Stack>
    )

  return (
    <Stack spacing={2}>
      <Typography variant='body3' color='text.secondary'>
        These images are for your reference only.
      </Typography>
      <Button variant='outlined' onClick={onAddImage} size='small' fullWidth>
        Add Image
      </Button>
      <Stack spacing={1.5}>
        <Typography variant='h6' color='text.primary'>
          Client Images
        </Typography>
        <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ justifyContent: 'flex-start' }}>
          {customerImages?.map((image, index) => (
            <Stack key={`customer-image-${image.id}`} sx={{ width: 'calc(50% - 6px)' }}>
              <Box
                component='img'
                src={uploadsUrl(image.url)}
                alt={image.description ?? `Customer image ${index + 1}`}
                title={image.description ?? undefined}
                onError={e => (e.currentTarget.src = ImageNotFound)}
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: theme => theme.shape.borderRadius,
                  border: 'n100',
                  display: 'block',
                  backgroundColor: 'common.white',
                }}
              />
              {image.description?.trim() ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: 'block', wordBreak: 'break-word' }}
                >
                  {image.description}
                </Typography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Stack spacing={1.5}>
        <Typography variant='h6' color='text.primary'>
          Garment Images
        </Typography>
        <Stack direction='row' flexWrap='wrap' gap={1.5} sx={{ justifyContent: 'flex-start' }}>
          {images.map((image, index) => (
            <Stack key={`uploaded-image-${image.id}`} sx={{ width: 'calc(50% - 6px)' }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component='img'
                  src={uploadsUrl(image.url)}
                  alt={image.description ?? `Garment image ${index + 1}`}
                  title={image.description ?? undefined}
                  onError={e => (e.currentTarget.src = ImageNotFound)}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: theme => theme.shape.borderRadius,
                    border: 'n100',
                    display: 'block',
                    backgroundColor: 'common.white',
                  }}
                />
                <IconButton
                  size='small'
                  onClick={() => setImageToDelete(image.id)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'common.white',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                  }}
                >
                  <Close fontSize='small' />
                </IconButton>
              </Box>
              {image.description?.trim() ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 0.5, display: 'block', wordBreak: 'break-word' }}
                >
                  {image.description}
                </Typography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Dialog open={uploadOpen} onClose={closeUploadDialog}>
        <fetcher.Form method='post' encType='multipart/form-data' onSubmit={closeUploadDialog}>
          <Stack spacing={2} p={2} sx={{ minWidth: { mobile: '100%', tablet: 360 } }}>
            <Typography variant='h6'>Upload image</Typography>
            <input type='hidden' name='intent' value='saveImages' />
            <input type='hidden' name='garmentId' value={String(garment.id)} />
            <input type='hidden' name='customerId' value={String(customerId)} />
            <Button component='label' variant='outlined' size='small' sx={{ alignSelf: 'flex-start' }}>
              Choose image
              <input type='file' name='image' hidden accept='image/*' onChange={handleFileChosen} />
            </Button>
            {pendingFile && (
              <img src={URL.createObjectURL(pendingFile)} alt={pendingFile.name} style={{ maxWidth: '100%' }} />
            )}
            <TextField name='description' label='Description' size='small' fullWidth multiline minRows={2} />
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button type='button' variant='outlined' onClick={closeUploadDialog}>
                Cancel
              </Button>
              <Button type='submit' variant='contained' disabled={!pendingFile}>
                Upload
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>

      <Dialog open={imageToDelete !== null} onClose={handleCloseDeleteDialog}>
        <fetcher.Form method='post' onSubmit={() => setImageToDelete(null)}>
          <input type='hidden' name='intent' value='deleteImage' />
          <input type='hidden' name='imageId' value={String(imageToDelete ?? '')} />
          <input type='hidden' name='garmentId' value={String(garment.id)} />
          <input type='hidden' name='customerId' value={String(customerId)} />
          <Stack spacing={6} p={2}>
            <Typography variant='h6'>Delete image?</Typography>
            <Typography>Are you sure you want to delete this image?</Typography>
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button variant='outlined' onClick={handleCloseDeleteDialog}>
                Cancel
              </Button>
              <Button type='submit' color='error'>
                Delete
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>
    </Stack>
  )
}

//* ============ DRAWER HELPER COMPONENTS ============ *//

function GarmentPreviewSection({ imageSrc, label }: { imageSrc: string; label: string }) {
  const { onMobile } = useGlobalState()
  const { ZoomDialog, zoomAction } = useZoomDialog(imageSrc, label)
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'grey.50',
        borderRadius: 2,
        p: 2,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: 180,
      }}
    >
      <ZoomDialog />
      <ActionGutter actions={[zoomAction]} />
      <img
        src={imageSrc}
        alt={label}
        style={{
          width: '100%',
          maxWidth: onMobile ? 140 : 220,
          maxHeight: onMobile ? 180 : 260,
          objectFit: 'contain',
          borderRadius: 8,
          border: 'n100',
          marginBottom: 8,
          background: 'common.white',
        }}
      />
      <Typography variant='body3' color='text.secondary' align='center'>
        {label}
      </Typography>
    </Box>
  )
}

function DrawerTabPill({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      variant='text'
      sx={{
        whiteSpace: 'nowrap',
        borderRadius: 999,
        px: 1.5,
        height: 32,
        bgcolor: active ? 'primary.main' : 'transparent',
        color: active ? 'common.white' : 'grey.900',
        '&:hover': { bgcolor: active ? 'primary.extraLight' : 'grey.100' },
      }}
    >
      {label}
    </Button>
  )
}

function StatusCard({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: 'common.white', border: 'n100', borderRadius: 1, flex: 1 }}>
      <Typography variant='caption' sx={{ letterSpacing: 0.6, color: 'text.secondary' }} display='block'>
        {label}
      </Typography>
      <Typography variant='body3' sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  )
}

function DetailRowsList({ rows }: { rows: Array<{ label: string; value: string }> }) {
  return (
    <Stack spacing={0.5} sx={{ '& .DetailRow:not(:last-child)': { borderBottom: 'n100' } }}>
      {rows.map((row, idx) => (
        <Stack key={idx} direction='row' justifyContent='space-between' className='DetailRow' py={1} flexShrink={1}>
          <Typography variant='body3' color='text.secondary' sx={{ textOverflow: { mobile: 'ellipsis' } }}>
            {row.label}
          </Typography>
          <Typography variant='body3' sx={{ textAlign: 'right' }}>
            {row.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

function KeyValue({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction='row' justifyContent='space-between'>
      <Typography variant='body3' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body3' sx={{ fontWeight: bold ? 700 : 400 }}>
        {value}
      </Typography>
    </Stack>
  )
}
